// "Variable" and "Custom" are not rows in prescription_frequencies - getFrequencies() only ever
// returns what the facility's company has defined, so these two only exist client side and have
// to be appended to every list fetched from /prescription/frequencies. When a screen forgot to
// append them, a variable dose chart opened with an empty frequency: the schedule grid's v-if
// requires a frequency object, so the week headers rendered with no table under them, and the
// submitted frequency_shorthand came out empty and failed validation.

export const VARIABLE_FREQUENCY = {
    frequency_name: 'Variable',
    interval: 1,
    period: 'variable',
    times: ['0600', '0800', '1000', '1200', '1700', '1800', '2000', '2200'],
    days_of_week: ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
};

export const CUSTOM_FREQUENCY = {
    frequency_name: 'Custom',
    interval: 1,
    period: 'weekly',
    times: ['0600', '0800', '1000', '1200', '1700', '1800', '2000', '2200'],
    days_of_week: ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
};

// 'variable' reaches the form three ways - the saved frequency, the saved frequency_shorthand,
// and the dropdown option's frequency_name ("Variable") - so every comparison has to ignore case.
// A chart stored with the capital was previously only detected through its dose time dates.
export function isVariableValue(value) {
    return typeof value === 'string' && value.toLowerCase() === 'variable';
}

// Variable reaches the form as a string on a saved chart and as the dropdown option object when
// the prescriber picks it from the frequency list. The object form matters: picking "Variable"
// from the dropdown does not set isVariableMode, so a guard that only looks at strings misses it
// and the chart saves with the object's period instead.
export function isVariableFrequency(frequency) {
    return isVariableValue(frequency)
        || isVariableValue(frequency?.period)
        || isVariableValue(frequency?.frequency_name);
}

// The frequency list as the prescribing form expects it: the facility's own frequencies plus the
// two client side options. Safe to call on a list that already contains them.
export function withLocalFrequencyOptions(frequencies) {
    const options = Array.isArray(frequencies) ? [...frequencies] : [];
    const has = (name) => options.some(
        (o) => o && typeof o.frequency_name === 'string' && o.frequency_name.toLowerCase() === name
    );

    if (!has('variable')) options.push({ ...VARIABLE_FREQUENCY });
    if (!has('custom')) options.push({ ...CUSTOM_FREQUENCY });

    return options;
}

// The option a variable dose chart should sit on. Prefers the facility's list so any local
// customisation wins, and falls back to the client side definition so the form is never left
// without a frequency object to render and submit from.
export function findVariableFrequency(frequencies) {
    const fromList = (frequencies || []).find(
        (o) => o && (isVariableValue(o.period) || isVariableValue(o.frequency_name))
    );
    return fromList || { ...VARIABLE_FREQUENCY };
}
